import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';

import Button from '../../../../../../../components/global/Button';
import CustomControl from '../../../../../../../components/global/CustomControl';
import PinField from '../../../../../../../components/global/PinField';
import WaldoSpeechBubble from '../../../../../../../components/global/WaldoSpeechBubble';
import { maskEmail } from '../../../../../../../util/email';
import { getObfuscatedPhoneNumber } from '../../../../../../../util/account';

const propTypes = {
  authMethodIsPhone: PropTypes.bool,
  emailAddress: PropTypes.string,
  phoneNumber: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.string,
  requireHumanInput: PropTypes.bool,
  blockLogin: PropTypes.bool,
  onVerify: PropTypes.func.isRequired,
  onPinResend: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  backLabel: PropTypes.string,
};

const PinVerifyStep = ({
  authMethodIsPhone,
  emailAddress,
  phoneNumber,
  loading,
  error,
  requireHumanInput,
  blockLogin,
  onVerify,
  onPinResend,
  onBack,
  backLabel = 'Choose another method',
}) => {
  const [code, setCode] = useState('');
  const [forceAllowAction, setForceAllowAction] = useState(false);
  const isValid = code.length === 4;
  // The platform challenged the pin request, so it can only be sent again once
  // the user has confirmed they are not a robot.
  const resendDisabled =
    loading || blockLogin || (requireHumanInput && !forceAllowAction);

  const maskedDestination = authMethodIsPhone
    ? getObfuscatedPhoneNumber(phoneNumber)
    : maskEmail(emailAddress);

  const handleSubmit = event => {
    event.preventDefault();
    onVerify(code);
  };

  return (
    <Fragment>
      {onBack ? (
        <Button
          btnStyle="reset"
          type="button"
          className="text-primary mb-3"
          onClick={onBack}
        >
          &larr; {backLabel}
        </Button>
      ) : null}
      <WaldoSpeechBubble className="w-100-i mx-0 mb-2 text-sm" position="right">
        Please provide the pin code sent to{' '}
        <span className="text-primary">{maskedDestination}</span>.
      </WaldoSpeechBubble>
      <div className="marketing-account-onboarding-pin-step">
        <h2 className="text-center mb-4">
          {authMethodIsPhone ? 'Verify your phone' : 'Verify your email'}
        </h2>

        <form onSubmit={handleSubmit} className="text-center">
          <div className="mb-4">
            <PinField
              autofocus
              onComplete={value => setCode(value)}
              onChange={value => setCode(value)}
            />
          </div>

          {requireHumanInput && (
            <div className="mb-3">
              <CustomControl
                type="checkbox"
                label="I'm not a robot"
                checked={forceAllowAction}
                onChange={() => setForceAllowAction(!forceAllowAction)}
              />
            </div>
          )}

          {error && <p className="text-danger small mb-3">{error}</p>}

          <Button
            type="submit"
            disabled={loading || !isValid || blockLogin}
            loading={loading}
            className="btn-primary w-100 mb-3"
            btnSize="lg"
          >
            Verify and view my photos
          </Button>

          <p className="text-muted small mb-2">
            By continuing you agree to our <a href="/terms">Terms of Use</a> &{' '}
            <a href="/privacy">Privacy Policy</a>. <br />
            This site is protected by reCAPTCHA.
          </p>

          <p>
            Didn&apos;t get it?{' '}
            <Button
              type="button"
              btnStyle="link"
              disabled={resendDisabled}
              className="p-0 align-baseline"
              onClick={() => onPinResend(forceAllowAction)}
            >
              Resend PIN
            </Button>
          </p>
        </form>
      </div>
    </Fragment>
  );
};

PinVerifyStep.propTypes = propTypes;

export default PinVerifyStep;
